/**
 * The translation layer: one instance for the page (like i18next's default
 * instance), readable from React through useTranslation() and from plain
 * modules through `i18n.t`.
 *
 * Missing keys: a key absent from the active catalog falls back to English at
 * runtime and is COUNTED (getMissingKeys). A key absent from English too
 * renders as the key itself. The counter is what test/i18n.test.tsx asserts
 * is empty after rendering every converted screen in Arabic.
 */
import { ar } from "./catalogs/ar";
import { en } from "./catalogs/en";
import {
  browserLocale,
  canonicalLocale,
  directionOf,
  formatLocaleOf,
  htmlLangOf,
  languageOf,
  resolveLocale,
  validTimeZone,
} from "./locale";
import type {
  Catalog,
  Language,
  MessageKey,
  TFunction,
  TParams,
} from "./types";

/** The user-facing product name; one place, so a rename is one line. */
export const PRODUCT_NAME = "Atlas";

/**
 * localStorage key for the language chosen on this device: the sign-in
 * switch, or the person's own saved locale. Never the workspace's.
 */
export const DEVICE_LOCALE_KEY = "atlas-device-locale";

/**
 * localStorage keys earlier slices wrote, deleted on load, not migrated:
 *
 * - "atlas-locale": the first slice stored the RESOLVED locale here,
 *   workspace fallback included, so an Egyptian workspace left "ar-EG" behind
 *   as if the person had chosen it.
 * - "atlas-personal-locale": the memberships this device saw save a locale in
 *   Profile, the guess the SPA made while GET /v1/users/me did not say whose
 *   locale it was. It now says (`personalLocale`, D28), on every device.
 */
export const RETIRED_LOCALE_KEYS = [
  "atlas-locale",
  "atlas-personal-locale",
] as const;

// U+2068 FIRST STRONG ISOLATE … U+2069 POP DIRECTIONAL ISOLATE. A code such
// as JE-2026-000003 or an English name inside an Arabic sentence (and the
// reverse) keeps its own direction instead of reordering the words around it.
const FSI = "⁨";
const PDI = "⁩";

function flatten(
  catalog: Catalog,
  prefix = "",
  out = new Map<string, string>(),
) {
  for (const [key, value] of Object.entries(catalog)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === "string") out.set(path, value);
    else flatten(value, path, out);
  }
  return out;
}

export interface TranslatorOptions {
  catalogs: Record<Language, Catalog>;
  onMissing?: (language: Language, key: string) => void;
}

export interface TranslateContext {
  language: Language;
  formatLocale: string;
}

/**
 * Pure translator over a set of catalogs — exported so the fallback and the
 * missing-key accounting can be tested with catalogs built for the test.
 */
export function createTranslator({ catalogs, onMissing }: TranslatorOptions) {
  const flat: Record<Language, Map<string, string>> = {
    en: flatten(catalogs.en),
    ar: flatten(catalogs.ar),
  };
  const pluralRules = new Map<string, Intl.PluralRules>();
  const plural = (language: Language, count: number) => {
    let rules = pluralRules.get(language);
    if (!rules) {
      rules = new Intl.PluralRules(language);
      pluralRules.set(language, rules);
    }
    return rules.select(count);
  };

  function lookup(language: Language, key: string, count?: number) {
    const table = flat[language];
    if (count !== undefined) {
      const form =
        table.get(`${key}_${plural(language, count)}`) ??
        table.get(`${key}_other`);
      if (form !== undefined) return form;
    }
    return table.get(key);
  }

  function exists(key: string): boolean {
    return lookup("en", key, 0) !== undefined || flat.en.has(`${key}_one`);
  }

  function interpolate(
    template: string,
    params: TParams,
    formatLocale: string,
  ): string {
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (whole, name: string) => {
      const value = name === "product" ? PRODUCT_NAME : params[name];
      if (value === undefined || value === null) return whole;
      if (typeof value === "number")
        return new Intl.NumberFormat(formatLocale).format(value);
      return `${FSI}${value}${PDI}`;
    });
  }

  function translate(
    ctx: TranslateContext,
    key: string,
    params: TParams & { defaultValue?: string } = {},
  ): string {
    const { count, defaultValue } = params;
    if (defaultValue !== undefined && !exists(key))
      return interpolate(defaultValue, params, ctx.formatLocale);
    let template = lookup(ctx.language, key, count);
    if (template === undefined) {
      onMissing?.(ctx.language, key);
      if (ctx.language !== "en") template = lookup("en", key, count);
      if (template === undefined && ctx.language !== "en")
        onMissing?.("en", key);
    }
    return interpolate(template ?? key, params, ctx.formatLocale);
  }

  return { translate, exists };
}

// ---- the page's instance ---------------------------------------------------

const missing = new Map<string, number>();
let warned = new Set<string>();

const translator = createTranslator({
  catalogs: { en, ar },
  onMissing(language, key) {
    const id = `${language}:${key}`;
    missing.set(id, (missing.get(id) ?? 0) + 1);
    if (import.meta.env?.DEV && !warned.has(id)) {
      warned.add(id);
      console.warn(`[i18n] missing ${language} message: ${key}`);
    }
  },
});

/** Keys looked up and not found since the last reset, as "lang:key" → count. */
export function getMissingKeys(): ReadonlyMap<string, number> {
  return missing;
}

export function resetMissingKeys(): void {
  missing.clear();
  warned = new Set();
}

export interface I18nSnapshot {
  /** The resolved BCP 47 locale, e.g. "ar-EG". */
  locale: string;
  /** The catalog in use. */
  language: Language;
  dir: "ltr" | "rtl";
  /** The value for <html lang>. */
  htmlLang: string;
  /** The tag formatters use (Gregorian; Latin digits for Arabic). */
  formatLocale: string;
  /** The saved IANA zone, or undefined for the browser's own. */
  timeZone: string | undefined;
  /** The person's own saved locale (TenantUser.locale), else null. */
  personLocale: string | null;
  t: TFunction;
}

interface Sources {
  /** The person's own saved locale: an explicit choice. */
  person: string | null;
  /** The workspace's locale: formatting conventions only. */
  workspace: string | null;
  userTimeZone: string | undefined;
  /** The language chosen on this device: an explicit choice. */
  device: string | null;
}

function readDeviceLocale(): string | null {
  try {
    for (const key of RETIRED_LOCALE_KEYS)
      globalThis.localStorage?.removeItem(key);
    return canonicalLocale(globalThis.localStorage?.getItem(DEVICE_LOCALE_KEY));
  } catch {
    return null;
  }
}

function writeDeviceLocale(locale: string): void {
  try {
    globalThis.localStorage?.setItem(DEVICE_LOCALE_KEY, locale);
  } catch {
    // Private mode or storage disabled: the choice lasts for this page only.
  }
}

let sources: Sources = {
  person: null,
  workspace: null,
  userTimeZone: undefined,
  device: readDeviceLocale(),
};
let snapshot = build();
const listeners = new Set<() => void>();

function build(): I18nSnapshot {
  const locale = resolveLocale({
    person: sources.person,
    device: sources.device,
    workspace: sources.workspace,
    browser: browserLocale(),
  });
  const ctx: TranslateContext = {
    language: languageOf(locale),
    formatLocale: formatLocaleOf(locale),
  };
  const t = ((key: string, params?: TParams & { defaultValue?: string }) =>
    translator.translate(ctx, key, params)) as TFunction;
  return {
    locale,
    language: ctx.language,
    dir: directionOf(locale),
    htmlLang: htmlLangOf(locale),
    formatLocale: ctx.formatLocale,
    timeZone: sources.userTimeZone,
    personLocale: sources.person,
    t,
  };
}

function update(next: Partial<Sources>): void {
  sources = { ...sources, ...next };
  const rebuilt = build();
  if (
    rebuilt.locale === snapshot.locale &&
    rebuilt.timeZone === snapshot.timeZone &&
    rebuilt.personLocale === snapshot.personLocale
  )
    return;
  snapshot = rebuilt;
  for (const listener of listeners) listener();
}

export const i18n = {
  getSnapshot: (): I18nSnapshot => snapshot,
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  get language(): Language {
    return snapshot.language;
  },
  dir: (): "ltr" | "rtl" => snapshot.dir,
  t: ((key: string, params?: TParams & { defaultValue?: string }) =>
    snapshot.t(key as MessageKey, params)) as TFunction,
  exists: (key: string): boolean => translator.exists(key),
  /**
   * The signed-in person's settings, or null on sign-out.
   *
   * `locale` is the person's OWN saved locale, or null when they have none
   * (GET /v1/users/me → personalLocale; ./sync.ts). It beats the
   * device's choice and is remembered on the device, so the next sign-in page
   * opens in it. `workspaceLocale` only supplies formatting conventions; it
   * never changes the language. `timeZone` is the zone dates are shown in.
   */
  setUserPreferences(
    prefs: {
      locale?: unknown;
      workspaceLocale?: unknown;
      timeZone?: unknown;
    } | null,
  ) {
    const person = prefs ? canonicalLocale(prefs.locale) : null;
    if (person) writeDeviceLocale(person);
    update({
      person,
      workspace: prefs ? canonicalLocale(prefs.workspaceLocale) : null,
      userTimeZone: prefs ? validTimeZone(prefs.timeZone) : undefined,
      ...(person ? { device: person } : {}),
    });
  },
  /** The device's locale (the sign-in page's language switch). */
  changeLanguage(locale: string): void {
    const device = canonicalLocale(locale);
    if (!device) return;
    writeDeviceLocale(device);
    update({ device });
  },
  /** Forget the device's choice (tests; a "use the default" control). */
  clearDeviceLanguage(): void {
    try {
      globalThis.localStorage?.removeItem(DEVICE_LOCALE_KEY);
    } catch {
      // Nothing stored.
    }
    update({ device: null });
  },
};

/** Formatter defaults, read by lib/money.ts on every call. */
export function getFormatSettings(): {
  formatLocale: string;
  timeZone: string | undefined;
} {
  return { formatLocale: snapshot.formatLocale, timeZone: snapshot.timeZone };
}

/**
 * Put the active locale on <html lang dir>, and the page's title and
 * description in its language (index.html holds only the product name, and
 * English for a reader with no script). Safe where there is no DOM.
 */
export function applyDocumentLocale(s: I18nSnapshot = snapshot): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (!root) return;
  root.lang = s.htmlLang;
  root.dir = s.dir;
  document.title = s.t("meta.title");
  if (typeof document.querySelector === "function")
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", s.t("meta.description"));
}
